import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createOrganization, type ErrorResponse } from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SUPPORTED_CURRENCIES, SUPPORTED_COUNTRIES, getCountryFlag } from '@/lib/utils';
import { LanguageSelector } from '@/components/ui/language-selector';

export default function CreateOrganizationPage() {
  const { t } = useTranslation(['admin', 'common', 'errors']);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [defaultCountry, setDefaultCountry] = useState('VN');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await createOrganization({
        body: {
          name,
          description: description || undefined,
          currency,
          defaultCountry,
        },
      });

      if (result.data) {
        navigate('/organizations');
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('organizations.failedToCreate'));
      } else {
        setError(t('organizations.failedToCreate'));
      }
    } catch (err) {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 p-4 md:p-8 relative">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link
            to="/organizations"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; {t('organizations.backToOrganizations')}
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('organizations.createOrganization')}</CardTitle>
            <CardDescription>
              {t('organizations.createDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('organizations.organizationName')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder={t('organizations.enterOrganizationName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('organizations.description')}</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLoading}
                  placeholder={t('organizations.enterDescription')}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">{t('organizations.currency')}</Label>
                <Select
                  value={currency}
                  onValueChange={setCurrency}
                  disabled={isLoading}
                >
                  <SelectTrigger id="currency">
                    <SelectValue placeholder={t('organizations.selectCurrency')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_CURRENCIES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {code} - {t(`currencies.${code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultCountry">{t('organizations.defaultCountry')}</Label>
                <Select
                  value={defaultCountry}
                  onValueChange={setDefaultCountry}
                  disabled={isLoading}
                >
                  <SelectTrigger id="defaultCountry">
                    <SelectValue placeholder={t('organizations.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_COUNTRIES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {getCountryFlag(code)} {t(`countries.${code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t('organizations.defaultCountryDescription')}
                </p>
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? t('organizations.creating') : t('organizations.createOrganization')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/organizations')}
                  disabled={isLoading}
                >
                  {t('common:buttons.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
